// frontend/src/i18n/errorCatalog.js
// SOURCE UNIQUE DE VÉRITÉ des messages d'erreur utilisateur (standards MS/AWS/Stripe).
// Le backend n'envoie qu'un `code` (+ requestId) ; le TEXTE vit ici, traduit à l'affichage
// selon la langue active (FR/EN/ES/AR). Aucun composant ne doit fabriquer de texte d'erreur.
//
// Chaque entrée : { severity, actions[] }  → le titre/description viennent d'i18n :
//   errors.catalog.<CODE>.title / .description   +   errors.action.<action>

export const SEVERITY = { INFO: 'info', WARNING: 'warning', ERROR: 'error', CRITICAL: 'critical' };

// Actions génériques réutilisables (l'id sert à câbler un handler dans l'UI).
// retry · retryLater · relogin · forgotPassword · contactAdmin · contactCompany · checkTicket · refreshStatus
export const ERROR_CATALOG = {
  // ── Auth ──
  AUTH_INVALID_CREDENTIALS: { severity: SEVERITY.WARNING, actions: ['retry', 'forgotPassword', 'contactAdmin'] },
  AUTH_INVALID_ADMIN_CODE:  { severity: SEVERITY.WARNING, actions: ['retry', 'contactAdmin'] },
  AUTH_PASSWORD_RESET:      { severity: SEVERITY.WARNING, actions: ['forgotPassword', 'contactAdmin'] },
  AUTH_WRONG_PORTAL:        { severity: SEVERITY.WARNING, actions: [] },
  AUTH_SESSION_EXPIRED:     { severity: SEVERITY.WARNING, actions: ['relogin'] },
  AUTH_ACCOUNT_LOCKED:      { severity: SEVERITY.ERROR,   actions: ['contactAdmin'] },
  AUTH_TOO_MANY_ATTEMPTS:   { severity: SEVERITY.WARNING, actions: ['retryLater'] },
  AUTH_FORBIDDEN:           { severity: SEVERITY.WARNING, actions: [] },
  // ── Réseau / serveur ──
  NETWORK_OFFLINE:          { severity: SEVERITY.WARNING, actions: ['retry'] },
  NETWORK_TIMEOUT:          { severity: SEVERITY.WARNING, actions: ['retry'] },
  SERVER_UNAVAILABLE:       { severity: SEVERITY.ERROR,   actions: ['retryLater'] },
  SERVER_ERROR:             { severity: SEVERITY.ERROR,   actions: ['retryLater'] },
  RATE_LIMIT:               { severity: SEVERITY.WARNING, actions: ['retryLater'] },
  // ── Données ──
  NOT_FOUND:                { severity: SEVERITY.INFO,    actions: [] },
  CONFLICT:                 { severity: SEVERITY.WARNING, actions: [] },
  VALIDATION:               { severity: SEVERITY.WARNING, actions: [] },
  BAD_REQUEST:              { severity: SEVERITY.WARNING, actions: ['retry'] },
  // ── Paiement ──
  PAYMENT_TIMEOUT:          { severity: SEVERITY.WARNING, actions: ['refreshStatus'] },
  PAYMENT_FAILED:           { severity: SEVERITY.ERROR,   actions: ['retry'] },
  // ── Réservation ──
  SEAT_UNAVAILABLE:         { severity: SEVERITY.WARNING, actions: [] },
  TRIP_FULL:                { severity: SEVERITY.INFO,    actions: [] },
  // ── QR / billets ──
  QR_ALREADY_USED:          { severity: SEVERITY.WARNING, actions: ['contactCompany'] },
  QR_INVALID:               { severity: SEVERITY.ERROR,   actions: ['checkTicket', 'contactCompany'] },
  // ── Repli ──
  GENERIC:                  { severity: SEVERITY.ERROR,   actions: ['retry'] },
};

// Backend `code` (ou statut HTTP) → code catalogue. Un seul endroit à maintenir.
const BACKEND_CODE_TO_APP = {
  INVALID_CREDENTIALS: 'AUTH_INVALID_CREDENTIALS',
  MISSING_IDENTIFIER:  'AUTH_INVALID_CREDENTIALS',
  MISSING_CREDENTIALS: 'AUTH_INVALID_CREDENTIALS',
  INVALID_ADMIN_CODE:  'AUTH_INVALID_ADMIN_CODE',
  PASSWORD_RESET_REQUIRED: 'AUTH_PASSWORD_RESET',
  WRONG_PORTAL:        'AUTH_WRONG_PORTAL',
  ACCOUNT_LOCKED:      'AUTH_ACCOUNT_LOCKED',
  ACCOUNT_SUSPENDED:   'AUTH_ACCOUNT_LOCKED',
  ACCOUNT_INACTIVE:    'AUTH_ACCOUNT_LOCKED',
  ACCOUNT_TEMPORARILY_BLOCKED: 'AUTH_ACCOUNT_LOCKED',
  TOO_MANY_ATTEMPTS:   'AUTH_TOO_MANY_ATTEMPTS',
  RATE_LIMIT_EXCEEDED: 'RATE_LIMIT',
  LOGIN_ERROR:         'SERVER_ERROR',
  SERVER_ERROR:        'SERVER_ERROR',
  SESSION_INVALIDATED: 'AUTH_SESSION_EXPIRED',
  TOKEN_EXPIRED:       'AUTH_SESSION_EXPIRED',
  INVALID_TOKEN:       'AUTH_SESSION_EXPIRED',
  VALIDATION_ERROR:    'VALIDATION',
  INVALID_DATA:        'VALIDATION',
  DUPLICATE_ENTRY:     'CONFLICT',
  FOREIGN_KEY_VIOLATION: 'BAD_REQUEST',
  INVALID_ID:          'BAD_REQUEST',
  DB_SCHEMA_ERROR:     'SERVER_ERROR',
  DB_TIMEOUT:          'SERVER_UNAVAILABLE',
  INTERNAL_ERROR:      'SERVER_ERROR',
  PAYMENT_FAILED:      'PAYMENT_FAILED',
  PAYMENT_CANCELLED:   'PAYMENT_FAILED',
  REFUND_FAILED:       'PAYMENT_FAILED',
  PAYMENT_TIMEOUT:     'PAYMENT_TIMEOUT',
  SEAT_UNAVAILABLE:    'SEAT_UNAVAILABLE',
  TRIP_FULL:           'TRIP_FULL',
  TRIP_NOT_FOUND:      'NOT_FOUND',
  RESERVATION_NOT_FOUND: 'NOT_FOUND',
  QR_ALREADY_USED:     'QR_ALREADY_USED',
  TICKET_ALREADY_USED: 'QR_ALREADY_USED',
  QR_INVALID:          'QR_INVALID',
  INVALID_QR:          'QR_INVALID',
};

const STATUS_TO_APP = {
  400: 'BAD_REQUEST', 401: 'AUTH_SESSION_EXPIRED', 403: 'AUTH_FORBIDDEN', 404: 'NOT_FOUND',
  408: 'NETWORK_TIMEOUT', 409: 'CONFLICT', 422: 'VALIDATION', 429: 'RATE_LIMIT',
  500: 'SERVER_ERROR', 502: 'SERVER_UNAVAILABLE', 503: 'SERVER_UNAVAILABLE', 504: 'NETWORK_TIMEOUT',
};

// Endpoints d'authentification : un 401 y signifie « identifiants incorrects », JAMAIS
// « session expirée » (il n'y a pas encore de session). Filet défensif si le backend
// omet le code.
const isAuthLoginUrl = (url = '') => /\/(auth\/login|auth\/signup|admin\/auth\/login)\b/.test(String(url));

// Erreur axios/native → code catalogue. Priorité : code backend > statut > réseau/timeout.
export const mapErrorToCode = (err) => {
  const backendCode = err?.response?.data?.code;
  if (backendCode && BACKEND_CODE_TO_APP[backendCode]) return BACKEND_CODE_TO_APP[backendCode];
  const status = err?.response?.status;
  if (!err?.response) {
    if (err?.code === 'ECONNABORTED') return 'NETWORK_TIMEOUT';
    return 'NETWORK_OFFLINE';
  }
  // 🔒 Sur un endpoint de login sans code backend : 401 = identifiants ; 429 = trop de tentatives.
  if (isAuthLoginUrl(err?.config?.url)) {
    if (status === 401) return 'AUTH_INVALID_CREDENTIALS';
    if (status === 429) return 'AUTH_TOO_MANY_ATTEMPTS';
  }
  if (status && STATUS_TO_APP[status]) return STATUS_TO_APP[status];
  if (status >= 500) return 'SERVER_ERROR';
  return 'GENERIC';
};

// Résout le contenu affichable d'un code catalogue via une fonction t (namespace 'common').
// Retourne { code, severity, title, description, actions:[{id,label}], retryable }.
export const resolveError = (appCode, t) => {
  const code = ERROR_CATALOG[appCode] ? appCode : 'GENERIC';
  const meta = ERROR_CATALOG[code];
  const tr = (k, d) => (typeof t === 'function' ? t(k, d) : d);
  return {
    code,
    severity: meta.severity,
    title:       tr(`errors.catalog.${code}.title`,       tr('errors.generic', 'Une erreur est survenue.')),
    description: tr(`errors.catalog.${code}.description`,  tr('errors.generic', 'Une erreur est survenue. Veuillez réessayer.')),
    actions: (meta.actions || []).map((id) => ({ id, label: tr(`errors.action.${id}`, id) })),
    retryable: (meta.actions || []).includes('retry') || (meta.actions || []).includes('retryLater'),
  };
};

export default ERROR_CATALOG;
